'use client';

import { ChevronDown, Search } from 'lucide-react';
import * as React from 'react';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import { BottomSheetDrawer } from '../bottom-sheet-drawer';
import { Input } from './input';
import { FieldDescription, fieldVariants } from './input-field';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useIsMobile } from '../../lib/hooks/use-mobile';
import { cn } from '@/lib/utils';

type CountryCode = RPNInput.Country;

type CountryOption = { value?: CountryCode; label: string };

type SelectedCountryOption = { value: CountryCode; label: string };

type CountrySelectCopy = {
  searchPlaceholder: string;
  listTitle: string;
  emptyLabel: string;
  closeLabel: string;
};

const FlagComponent = ({
  country,
  countryName,
  className,
}: RPNInput.FlagProps & { className?: string }) => {
  const Flag = flags[country];

  return (
    <span
      className={cn(
        'flex h-5 w-7 shrink-0 overflow-hidden rounded-sm bg-foreground/20',
        className,
      )}
    >
      {Flag && <Flag title={countryName} />}
    </span>
  );
};
FlagComponent.displayName = 'FlagComponent';

const CountryList = ({
  options,
  value,
  onSelect,
  searchPlaceholder,
  emptyLabel,
  scrollList = true,
  className,
}: {
  options: CountryOption[];
  value?: CountryCode;
  onSelect: (country: CountryCode) => void;
  searchPlaceholder: string;
  emptyLabel: string;
  scrollList?: boolean;
  className?: string;
}) => {
  const [query, setQuery] = React.useState('');
  const listRef = React.useRef<HTMLDivElement>(null);

  const items = React.useMemo(() => {
    const available = options.filter(
      (option): option is SelectedCountryOption => Boolean(option.value),
    );
    const ordered = [
      ...available.filter((option) => option.value === value),
      ...available.filter((option) => option.value !== value),
    ];
    const needle = query.trim().toLowerCase().replace(/^\+/, '');

    if (!needle) {
      return ordered;
    }

    return ordered.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        RPNInput.getCountryCallingCode(option.value).startsWith(needle),
    );
  }, [options, query, value]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }

    const nodes = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[data-country]') ??
        [],
    );

    if (nodes.length === 0) {
      return;
    }

    event.preventDefault();
    const current = nodes.indexOf(document.activeElement as HTMLButtonElement);
    const isDown = event.key === 'ArrowDown';

    if (current === -1) {
      nodes[isDown ? 0 : nodes.length - 1]?.focus();
      return;
    }

    nodes[(current + (isDown ? 1 : -1) + nodes.length) % nodes.length]?.focus();
  };

  const rows =
    items.length === 0 ? (
      <p className="p-4 text-base text-text-subtle leading-6">{emptyLabel}</p>
    ) : (
      items.map((option) => {
        const isSelected = option.value === value;

        return (
          <button
            aria-selected={isSelected}
            className="flex w-full items-center gap-2 rounded-lg p-4 text-left transition-colors hover:bg-background-fill-primary-hover focus-visible:bg-background-fill-primary-hover focus-visible:outline-none"
            data-country={option.value}
            key={option.value}
            onClick={() => onSelect(option.value)}
            role="option"
            type="button"
          >
            <span className="flex size-6 shrink-0 items-center justify-center">
              <span
                className={cn(
                  'flex size-5 items-center justify-center rounded-full border border-border bg-background-fill-primary',
                  isSelected && 'border-brand-green2-300 bg-brand-green2-300',
                )}
              >
                {isSelected && (
                  <span className="size-2 rounded-full bg-background-fill-primary" />
                )}
              </span>
            </span>
            <FlagComponent country={option.value} countryName={option.label} />
            <span className="min-w-0 flex-1 truncate font-medium text-base text-text leading-6">
              {option.label}
            </span>
            <span className="shrink-0 text-base text-text leading-6">
              {`+${RPNInput.getCountryCallingCode(option.value)}`}
            </span>
          </button>
        );
      })
    );

  const list = (
    <div
      className={cn(
        scrollList &&
          // Thin bar with a transparent track instead of the default grey
          // gutter. The DS ScrollArea is not an option here: its viewport is
          // `h-full`, so it only scrolls at a fixed height and the menu has to
          // shrink to fit the filtered list.
          'min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-color:var(--color-border)_transparent] [scrollbar-width:thin]',
      )}
      ref={listRef}
      role="listbox"
    >
      {rows}
    </div>
  );

  return (
    <div
      className={cn('flex min-h-0 flex-col gap-0.5', className)}
      onKeyDown={handleKeyDown}
    >
      <div className="flex min-h-14 shrink-0 items-center gap-3 rounded-xl border border-border bg-background-fill-primary px-4 py-2">
        <Search aria-hidden="true" className="size-6 shrink-0 text-icon" />
        <Input
          className="text-base leading-6 text-text placeholder:text-text-subtle"
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          type="text"
          value={query}
          variant="ghost"
        />
      </div>

      {list}
    </div>
  );
};

type CountrySelectProps = CountrySelectCopy & {
  value?: CountryCode;
  options: CountryOption[];
  onChange: (country: CountryCode) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  'aria-label'?: string;
};

const CountrySelect = ({
  value,
  options,
  onChange,
  onFocus,
  onBlur,
  disabled,
  readOnly,
  searchPlaceholder,
  listTitle,
  emptyLabel,
  closeLabel,
  'aria-label': ariaLabel,
}: CountrySelectProps) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  const [menuWidth, setMenuWidth] = React.useState<number>();
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const handleSelect = (country: CountryCode) => {
    onChange(country);
    setOpen(false);
  };

  // The menu spans the whole field, not just the prefix button it hangs off,
  // so measure the field the trigger sits in every time the menu opens.
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setMenuWidth(triggerRef.current?.parentElement?.offsetWidth);
    }
    setOpen(next);
  };

  const trigger = (
    <button
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-label={ariaLabel}
      className="flex shrink-0 items-center gap-2 self-stretch border-border border-r pr-2 pl-4 text-text-subtle outline-none disabled:cursor-not-allowed"
      disabled={disabled || readOnly}
      onBlur={onBlur}
      onFocus={onFocus}
      ref={triggerRef}
      type="button"
    >
      {value && <FlagComponent country={value} countryName={value} />}
      <span className="whitespace-nowrap pr-1 text-base leading-6">
        {value ? `+${RPNInput.getCountryCallingCode(value)}` : ''}
      </span>
      <span className="flex size-6 items-center justify-center">
        <ChevronDown aria-hidden="true" className="size-4 text-icon" />
      </span>
    </button>
  );

  if (isMobile) {
    return (
      <BottomSheetDrawer
        closeLabel={closeLabel}
        content={
          <CountryList
            className="p-2"
            emptyLabel={emptyLabel}
            onSelect={handleSelect}
            options={options}
            scrollList={false}
            searchPlaceholder={searchPlaceholder}
            value={value}
          />
        }
        fullHeight
        onOpenChange={handleOpenChange}
        open={open}
        title={listTitle}
        trigger={trigger}
      />
    );
  }

  return (
    <PopoverPrimitive.Root onOpenChange={handleOpenChange} open={open}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Content
        align="start"
        alignOffset={-1}
        className="z-50 rounded-xl border border-border bg-background-fill-primary p-4 shadow-[0_2px_1.5px_0_rgba(0,0,0,0.08)] outline-hidden data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
        sideOffset={8}
        style={{ width: menuWidth }}
      >
        <CountryList
          className="max-h-[min(60vh,320px)]"
          emptyLabel={emptyLabel}
          onSelect={handleSelect}
          options={options}
          searchPlaceholder={searchPlaceholder}
          value={value}
        />
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Root>
  );
};

type PhoneFieldContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  invalid?: boolean;
  disabled?: boolean;
};

const PhoneFieldContainer = React.forwardRef<
  HTMLDivElement,
  PhoneFieldContainerProps
>(({ className, invalid, disabled, ...props }, ref) => (
  <div
    className={cn(
      fieldVariants({ disabled, invalid, leading: true }),
      className,
    )}
    ref={ref}
    {...props}
  />
));
PhoneFieldContainer.displayName = 'PhoneFieldContainer';

type PhoneNumberInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  invalid?: boolean;
};

const PhoneNumberInput = React.forwardRef<
  HTMLInputElement,
  PhoneNumberInputProps
>(({ className, id, invalid, label, required, ...props }, ref) => (
  <div className="relative min-w-0 flex-1 pl-4">
    <Input
      {...props}
      aria-invalid={invalid || undefined}
      aria-required={required || undefined}
      className={cn(
        'peer h-full truncate pt-6 pb-1 font-medium text-base text-text leading-6 placeholder:text-transparent focus:placeholder:text-text-subtle',
        invalid && 'text-text-danger',
        className,
      )}
      id={id}
      placeholder=" "
      ref={ref}
      required={required}
      variant="ghost"
    />
    <label
      className={cn(
        'pointer-events-none absolute top-2 right-0 left-4 truncate text-sm leading-5.5 transition-all duration-200',
        'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:leading-6',
        'peer-focus:top-2 peer-focus:translate-y-0 peer-focus:text-sm peer-focus:leading-5.5',
        invalid ? 'text-text-danger' : 'text-text-subtle',
      )}
      htmlFor={id}
    >
      {label}
      {required ? ' *' : ''}
    </label>
  </div>
));
PhoneNumberInput.displayName = 'PhoneNumberInput';

type PhoneInputProps = CountrySelectCopy & {
  label: string;
  labels?: RPNInput.Labels;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  defaultCountry?: CountryCode;
  countries?: CountryCode[];
  hint?: string;
  error?: string;
  invalid?: boolean;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  autoComplete?: string;
  className?: string;
  'data-pw'?: string;
};

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      label,
      labels,
      value,
      onChange,
      onBlur,
      defaultCountry = 'PL',
      countries,
      hint,
      error,
      invalid = false,
      disabled = false,
      required = false,
      id,
      name,
      autoComplete = 'tel',
      className,
      searchPlaceholder,
      listTitle,
      emptyLabel,
      closeLabel,
      'data-pw': dataPw,
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const fieldId = id ?? generatedId;
    const descriptionId = `${fieldId}-description`;

    const isInvalid = invalid || Boolean(error);
    const description = error ?? hint;

    return (
      <div className={cn('flex w-full flex-col gap-1', className)}>
        <RPNInput.default
          addInternationalOption={false}
          autoComplete={autoComplete}
          containerComponent={PhoneFieldContainer}
          containerComponentProps={{ disabled, invalid: isInvalid }}
          countries={countries}
          countrySelectComponent={CountrySelect}
          countrySelectProps={{
            closeLabel,
            emptyLabel,
            listTitle,
            searchPlaceholder,
          }}
          defaultCountry={defaultCountry}
          disabled={disabled}
          flagComponent={FlagComponent}
          inputComponent={PhoneNumberInput}
          international={false}
          labels={labels}
          name={name}
          numberInputProps={{
            'aria-describedby': description ? descriptionId : undefined,
            'data-pw': dataPw,
            id: fieldId,
            invalid: isInvalid,
            label,
            required,
          }}
          onBlur={onBlur}
          ref={ref as never}
          onChange={(next) => onChange(next ?? '')}
          value={value}
        />

        {description && (
          <FieldDescription id={descriptionId} invalid={isInvalid}>
            {description}
          </FieldDescription>
        )}
      </div>
    );
  },
);
PhoneInput.displayName = 'PhoneInput';

export { PhoneInput, FlagComponent, type CountryCode };
